/*
 * ____   ____    .__   __
 * \   \ /   /___ |  |_/  |______      ____ ___.__.
 *  \   Y   /  _ \|  |\   __  \_ \   / ___<   |  |
 *   \     (  <_> )  |_|  |  / __ \_/ /_/  >___  |
 *    \___/ \____/|____/__| (____  /\___  // ____|
 *                               \//_____/ \/
 *
 * Copyright (c) 2017-2025 Voltagy. All rights reserved.
 * https://voltagy.com
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the proprietary and confidential property of Voltagy.
 * Unauthorized copying, transfer, or use of this file, via any medium, is
 * strictly prohibited without the express written permission of Voltagy.
 *
 * The receipt or possession of this source code and/or related information
 * does not convey or imply any right to use it, reproduce it, disclose its
 * contents, or to manufacture, use, or sell anything that it may describe.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS"
 * AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE
 * IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE
 * ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE
 * LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR
 * CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF
 * SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS
 * INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN
 * CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE)
 * ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 *
 */

import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  Suspense,
  lazy,
  useRef,
} from "react";
import { I18nextProvider } from "react-i18next";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
} from "react-router-dom";

import AppUrlListener from "../components/AppUrlListener";
import ErrorBoundary from "../components/ErrorBoundary";
import OfflineStatusHandler from "../components/OfflineStatusHandler";
import SessionManagement from "../components/SessionManagement";
import { WebAuthnProvider } from "../context/WebAuthnContext";
import { useAuth } from "../hooks/useAuth";
import { AuthProvider } from "../providers/AuthProvider";
import { ConfigProvider } from "../providers/ConfigProvider";
import { DataProvider } from "../providers/DataProvider";
import { GlobalFiltersProvider } from "../providers/GlobalFiltersProvider";
import { OfflineCacheProvider } from "../providers/OfflineCacheProvider";
import { SyncProvider } from "../providers/SyncProvider";
import {
  UserPreferencesProvider,
  useUserPreferences,
} from "../providers/UserPreferencesProvider";
import { isMobile } from "../utils/DeviceUtilities";
import i18n from "../utils/i18n";

import "react-toastify/dist/ReactToastify.css";

const LoadingScreen = lazy(() =>
  import(/* webpackChunkName: "loading-screen" */ "./routes/LoadingScreen")
);
const LoginPage = lazy(() =>
  import(/* webpackChunkName: "login-page" */ "./routes/LoginPage")
);
const ReportPage = lazy(() =>
  import(/* webpackChunkName: "report-page" */ "./routes/ReportPage")
);
const Dashboard = lazy(() =>
  import(/* webpackChunkName: "dashboard" */ "./routes/Dashboard")
);
const JobsPage = lazy(() =>
  import(/* webpackChunkName: "jobs-page" */ "./routes/JobsPage")
);
const ErrorScreen = lazy(() =>
  import(/* webpackChunkName: "error-screen" */ "./routes/ErrorScreen")
);
const SSOCallback = lazy(() =>
  import(/* webpackChunkName: "sso-callback" */ "./routes/SSOCallback")
);
const SSOLogoutCallback = lazy(() =>
  import(
    /* webpackChunkName: "sso-logout-callback" */ "./routes/SSOLogoutCallback"
  )
);

const ssoSettingsCache = {
  promise: null,
  result: null,
};

const getSSO = async () => {
  if (ssoSettingsCache.result) {
    return ssoSettingsCache.result;
  }

  if (!ssoSettingsCache.promise) {
    try {
      const { getPublicSSOSettings } = await import(
        "../features/configuration/services/SSOService"
      );
      ssoSettingsCache.promise = getPublicSSOSettings()
        .then((result) => {
          ssoSettingsCache.result = result || { enabled: false, provider: "" };
          return ssoSettingsCache.result;
        })
        .catch(() => {
          return { enabled: false, provider: "" };
        });
    } catch (err) {
      return { enabled: false, provider: "" };
    }
  }

  return ssoSettingsCache.promise;
};

const ProtectedRoute = ({ children, darkMode }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen darkMode={darkMode} />;
  }

  if (!isAuthenticated) {
    return <Navigate replace state={{ from: location }} to="/login" />;
  }

  return children;
};

const AppRoutes = ({
  darkModePreferences,
  handleDarkModePreferencesChange,
  ssoEnabled,
  ssoProvider,
}) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  const isLocalhost = useMemo(() => {
    return (
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1"
    );
  }, []);

  const isProduction = useMemo(() => {
    return process.env.NODE_ENV === "production";
  }, []);

  const isDarkMode = darkModePreferences.darkMode === "dark";

  return (
    <Routes>
      {isAuthenticated ? (
        <>
          <Route
            element={
              <Navigate replace to={isMobile() ? "/jobs" : "/dashboard"} />
            }
            path="/"
          />
          <Route
            element={
              <ProtectedRoute darkMode={isDarkMode}>
                <Dashboard
                  darkModePreferences={
                    darkModePreferences || {
                      darkMode: "light",
                      followSystemTheme: true,
                    }
                  }
                  handleDarkModePreferencesChange={
                    handleDarkModePreferencesChange
                  }
                />
              </ProtectedRoute>
            }
            path="/dashboard"
          />
          <Route
            element={
              <ProtectedRoute darkMode={isDarkMode}>
                <JobsPage
                  darkModePreferences={
                    darkModePreferences || {
                      darkMode: "light",
                      followSystemTheme: true,
                    }
                  }
                  handleDarkModePreferencesChange={
                    handleDarkModePreferencesChange
                  }
                />
              </ProtectedRoute>
            }
            path="/jobs"
          />
        </>
      ) : (
        <>
          <Route
            element={
              <LoginPage
                darkMode={isDarkMode}
                isLocalhost={isLocalhost}
                isProduction={isProduction}
                ssoEnabled={ssoEnabled}
                ssoProvider={ssoProvider}
              />
            }
            path="/login"
          />
          <Route
            element={<SSOCallback darkMode={isDarkMode} />}
            path="/sso/:provider/callback"
          />
          <Route
            element={<SSOLogoutCallback darkMode={isDarkMode} />}
            path="/sso/:provider/logout-callback"
          />
        </>
      )}
      <Route
        element={
          <ProtectedRoute darkMode={isDarkMode}>
            <ReportPage darkMode={isDarkMode} />
          </ProtectedRoute>
        }
        path="/report/:id"
      />
      <Route
        element={
          <ErrorScreen
            darkMode={isDarkMode}
            error={{
              message:
                new URLSearchParams(location.search).get("message") ||
                "An unknown error occurred",
            }}
          />
        }
        path="/error"
      />
      <Route
        element={
          isAuthenticated ? (
            <Navigate replace to="/" />
          ) : (
            <Navigate replace state={{ from: location }} to="/login" />
          )
        }
        path="*"
      />
    </Routes>
  );
};

const setupDarkModePreferences = () => {
  const savedPreferences = localStorage.getItem("darkModePreferences");
  if (savedPreferences) {
    const parsed = JSON.parse(savedPreferences);
    if (parsed.followSystemTheme) {
      const systemPreference = window.matchMedia("(prefers-color-scheme: dark)")
        .matches
        ? "dark"
        : "light";
      return {
        ...parsed,
        darkMode: systemPreference,
      };
    }
    return parsed;
  } else {
    const systemPreference = window.matchMedia("(prefers-color-scheme: dark)")
      .matches
      ? "dark"
      : "light";
    return {
      darkMode: systemPreference,
      followSystemTheme: true,
    };
  }
};

const PreferenceSync = ({ darkModePreferences, onPreferencesChange }) => {
  const { registerPreferenceSync, userPreferences, loading } =
    useUserPreferences();
  const initializedRef = useRef(false);

  useEffect(() => {
    const updateLocalValue = registerPreferenceSync(
      "darkModePreferences",
      darkModePreferences,
      onPreferencesChange
    );

    if (darkModePreferences) {
      updateLocalValue(darkModePreferences);
    }

    if (!loading && !initializedRef.current) {
      if (
        userPreferences.darkModePreferences &&
        JSON.stringify(userPreferences.darkModePreferences) !==
          JSON.stringify(darkModePreferences)
      ) {
        onPreferencesChange(userPreferences.darkModePreferences);
      }
      initializedRef.current = true;
    }
  }, [
    darkModePreferences,
    onPreferencesChange,
    registerPreferenceSync,
    userPreferences,
    loading,
  ]);

  return null;
};

const App = () => {
  const [darkModePreferences, setDarkModePreferences] = useState(
    setupDarkModePreferences
  );
  const [ssoSettings, setSSOSettings] = useState({
    enabled: false,
    provider: "",
  });
  const [cssApplied, setCssApplied] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => {
      if (darkModePreferences.followSystemTheme) {
        const newDarkMode = mediaQuery.matches ? "dark" : "light";
        setDarkModePreferences((prev) => ({
          ...prev,
          darkMode: newDarkMode,
        }));
      }
    };

    mediaQuery.addEventListener("change", handleChange);

    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [darkModePreferences.followSystemTheme]);

  const isDarkMode = darkModePreferences.darkMode === "dark";

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [isDarkMode]);

  useEffect(() => {
    let isMounted = true;

    const loadSSO = async () => {
      try {
        const settings = await getSSO();
        if (isMounted) {
          setSSOSettings(settings);
        }
      } catch (error) {
        if (isMounted) {
          setSSOSettings({ enabled: false, provider: "" });
        }
      }
    };

    loadSSO();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleDarkModePreferencesChange = useCallback((newPreferences) => {
    if (newPreferences.followSystemTheme) {
      const systemPreference = window.matchMedia("(prefers-color-scheme: dark)")
        .matches
        ? "dark"
        : "light";
      newPreferences.darkMode = systemPreference;
    }
    setDarkModePreferences(newPreferences);
    localStorage.setItem("darkModePreferences", JSON.stringify(newPreferences));
  }, []);

  const memoizedDarkModePreferences = useMemo(
    () => darkModePreferences,
    [darkModePreferences]
  );

  useEffect(() => {
    if (!cssApplied) {
      const style = document.createElement("style");
      style.textContent = "* { touch-action: manipulation; }";
      document.head.appendChild(style);
      setCssApplied(true);

      return () => {
        document.head.removeChild(style);
      };
    }
  }, [cssApplied]);

  return (
    <I18nextProvider i18n={i18n}>
      <ErrorBoundary darkMode={isDarkMode}>
        <Router>
          <AuthProvider
            ssoEnabled={ssoSettings.enabled}
            ssoProvider={ssoSettings.provider}
          >
            <WebAuthnProvider>
              <ConfigProvider darkMode={isDarkMode}>
                <DataProvider>
                  <OfflineCacheProvider>
                    <SyncProvider>
                      <UserPreferencesProvider>
                        <GlobalFiltersProvider>
                          <div data-testid="app-content">
                            <OfflineStatusHandler />
                            <SessionManagement />
                            <PreferenceSync
                              darkModePreferences={memoizedDarkModePreferences}
                              onPreferencesChange={
                                handleDarkModePreferencesChange
                              }
                            />
                            <AppUrlListener />
                            <Suspense
                              fallback={<LoadingScreen darkMode={isDarkMode} />}
                            >
                              <AppRoutes
                                darkModePreferences={
                                  memoizedDarkModePreferences
                                }
                                handleDarkModePreferencesChange={
                                  handleDarkModePreferencesChange
                                }
                                ssoEnabled={ssoSettings.enabled}
                                ssoProvider={ssoSettings.provider}
                              />
                            </Suspense>
                          </div>
                        </GlobalFiltersProvider>
                      </UserPreferencesProvider>
                    </SyncProvider>
                  </OfflineCacheProvider>
                </DataProvider>
              </ConfigProvider>
            </WebAuthnProvider>
          </AuthProvider>
        </Router>
      </ErrorBoundary>
    </I18nextProvider>
  );
};

export default App;
